/**
 * Shared styling and tone resolution for AbilityLifecycleMonitor and TargetLifecycleMonitor.
 */
import React from 'react';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  PLAYER_UI,
  RADII,
  SPACE,
} from '../theme';
import type { CastStatus, LifecycleCategory } from '../hooks/useCastLifecycleStream';

export function statusTone(status: CastStatus): 'success' | 'info' | 'danger' | 'neutral' {
  switch (status) {
    case 'completed': return 'success';
    case 'in-flight': return 'info';
    case 'rejected':  return 'danger';
    case 'expired':   return 'neutral';
  }
}

export function statusGlyph(status: CastStatus): string {
  switch (status) {
    case 'in-flight': return '◐';
    case 'completed': return '✓';
    case 'rejected':  return '✗';
    case 'expired':   return '⊙';
  }
}

export function categoryColor(category: LifecycleCategory): string {
  switch (category) {
    case 'cast':       return PLAYER_UI.brassBright;
    case 'vfx':        return COLORS.info;
    case 'combat':     return COLORS.warning;
    case 'projectile': return '#cc88ff';
    case 'rejection':  return COLORS.danger;
  }
}

export const lifecycleStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: SPACE.sm,
    gap: SPACE.sm,
    boxSizing: 'border-box',
    overflowY: 'hidden',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: SPACE.xs,
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingRight: 2,
  },
  empty: {
    color: COLORS.textMuted,
    textAlign: 'center',
    padding: SPACE.xl,
    fontStyle: 'italic',
    fontSize: FONT_SIZE.xs,
  },
  groupCard: {
    background: 'rgba(15, 20, 28, 0.75)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    overflow: 'hidden',
    transition: 'all 0.15s ease',
  },
  groupHeader: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: '6px 8px',
    color: COLORS.textPrimary,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    fontSize: FONT_SIZE.xs,
  },
  groupDetails: {
    background: 'rgba(0, 0, 0, 0.45)',
    borderTop: `1px solid ${COLORS.borderSubtle}`,
    padding: '6px 8px',
    fontSize: 11,
    fontFamily: FONTS.mono,
  },
  rejectionBanner: {
    color: COLORS.danger,
    background: 'rgba(255, 107, 107, 0.12)',
    border: `1px solid rgba(255, 107, 107, 0.3)`,
    padding: '4px 6px',
    borderRadius: RADII.sm,
    marginBottom: 4,
  },
  anomalyBanner: {
    color: COLORS.warning,
    background: 'rgba(241, 196, 15, 0.12)',
    border: `1px solid rgba(241, 196, 15, 0.3)`,
    padding: '4px 6px',
    borderRadius: RADII.sm,
    marginBottom: 4,
  },
};
